import { type FC, memo, useEffect, useRef, useState } from "react";
import { MarkdownContent } from "@9amhealth/shared";
import {
  CheckmarkMessageDelivered,
  CheckmarkMessageSent
} from "src/constants/icons";
import AvatarCubit from "src/state/AvatarCubit/AvatarCubit";
import { useBloc } from "src/state/state";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { useBlocNext } from "@blac/react";
import type { ChatMessage } from "../Chat/ChatBloc";
import { ChatBloc } from "../Chat/ChatBloc";
import { formatMessageTime } from "./state/utils";
import MessageFiles from "./MessageFiles";
import type { LeapChatFileRef } from "./state/types";
import {
  LeapChatContainer,
  LeapChatViewport,
  MessageListContent,
  AssistantMessageRoot,
  AssistantAvatar,
  AssistantMessageBody,
  AssistantMessageContent,
  UserMessageRoot,
  UserMessageContent,
  MessageFooter,
  FooterName,
  ComposerCardArea,
  HeroInputCard,
  HeroInput,
  ComposerIconButton
} from "./leapChat.styles";

const SendIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const AttachIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

const toFileRefs = (
  attachments: ChatMessage["computed"]["fileAttachments"]
): LeapChatFileRef[] =>
  attachments.map((a) => ({
    fileId: a.source,
    name: a.name ?? null,
    type: a.type ?? null,
    size: a.size ? Number(a.size) : null
  }));

const LegacyUserBubble: FC<{ msg: ChatMessage }> = memo(({ msg }) => (
  <UserMessageRoot>
    <UserMessageContent>
      {msg.computed.fileAttachments.length > 0 && (
        <MessageFiles files={toFileRefs(msg.computed.fileAttachments)} />
      )}
      {msg.text && <MarkdownContent text={msg.text} />}
    </UserMessageContent>
    <MessageFooter className="user-footer">
      {formatMessageTime(new Date(msg.timestamp).getTime())}
      <span className="confirmation">
        {msg.computed.isLocal ? (
          <CheckmarkMessageSent />
        ) : (
          <CheckmarkMessageDelivered />
        )}
      </span>
    </MessageFooter>
  </UserMessageRoot>
));

const LegacyAssistantBubble: FC<{ msg: ChatMessage }> = memo(({ msg }) => {
  const [avatar] = useBloc(AvatarCubit, {
    create: () => new AvatarCubit(msg.apiItem?.senderId ?? "")
  });
  const avatarUrl = avatar.path ?? "/9amAvatar.png";

  return (
    <AssistantMessageRoot>
      <AssistantMessageBody>
        <AssistantMessageContent>
          {msg.computed.fileAttachments.length > 0 && (
            <MessageFiles files={toFileRefs(msg.computed.fileAttachments)} />
          )}
          {msg.text && <MarkdownContent text={msg.text} />}
        </AssistantMessageContent>
        {msg.computed.senderName && (
          <MessageFooter>
            <AssistantAvatar>
              <img
                src={avatarUrl}
                alt={msg.computed.senderName ?? "9am.health"}
              />
            </AssistantAvatar>
            <FooterName>{msg.computed.senderName}</FooterName>
            {msg.computed.senderDescription
              ? ` · ${msg.computed.senderDescription}`
              : null}
            <span className="footer-time">
              {formatMessageTime(new Date(msg.timestamp).getTime())}
            </span>
          </MessageFooter>
        )}
      </AssistantMessageBody>
    </AssistantMessageRoot>
  );
});

const LegacyComposer: FC<{ chatBloc: ChatBloc }> = ({ chatBloc }) => {
  const [inputValue, setInputValue] = useState("");
  const [files, setFiles] = useState<FileList | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed && !files) {
      return;
    }
    void chatBloc.postMessage(trimmed, files);
    setInputValue("");
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = Boolean(inputValue.trim() || files);

  return (
    <HeroInputCard data-multiline={inputValue.includes("\n") ? "" : undefined}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => setFiles(e.target.files)}
      />
      <ComposerIconButton
        type="button"
        onClick={() => fileInputRef.current?.click()}
      >
        <AttachIcon />
      </ComposerIconButton>
      <HeroInput
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Message your care team…"
        rows={1}
      />
      <ComposerIconButton
        className="send-btn"
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        data-active={canSend ? "" : undefined}
      >
        <SendIcon />
      </ComposerIconButton>
    </HeroInputCard>
  );
};

const LegacyChatView: FC = () => {
  const [{ messages }, chatBloc] = useBlocNext(ChatBloc);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatBloc.initChat({ userId: StorageController.activeUserId ?? undefined });
    return () => {
      chatBloc.setChatOpen(false);
    };
  }, [chatBloc]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const visibleMessages = messages.filter((m) => m.placeholder === undefined);

  return (
    <LeapChatContainer>
      <LeapChatViewport ref={scrollRef}>
        <MessageListContent ref={contentRef}>
          {visibleMessages.map((msg) =>
            msg.computed.sentByUser ? (
              <LegacyUserBubble key={msg.id} msg={msg} />
            ) : (
              <LegacyAssistantBubble key={msg.id} msg={msg} />
            )
          )}
        </MessageListContent>
        <ComposerCardArea ref={composerRef}>
          <LegacyComposer chatBloc={chatBloc} />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default LegacyChatView;
